import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../navigation/Navbar';
import Loader from '../common/PageLoader';
import './styles/notifications.css';
import { mainGet } from '../../domain/net/mainApi';

const NOTIFICATIONS_PER_PAGE = 10;

export default function Notifications() {
    const [allNotifications, setAllNotifications] = useState([]);
    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const fetchUserData = useCallback(async () => {
        try {
            const clid = localStorage.getItem('clid');
            const key = localStorage.getItem('key');
            const response = await mainGet({ type: 'checkuser', id: clid, pass: key });

            if (response.data.ok) {
                setUser(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch user data:', err);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const clid = localStorage.getItem('clid');
            const response = await mainGet({ type: 'getNotif', to: clid });

            if (response.data.ok) {
                const notifications = response.data.data || [];

                const sortedNotifications = notifications.sort((a, b) =>
                    new Date(b.date) - new Date(a.date)
                );

                setAllNotifications(sortedNotifications );
                // Показываем первые 10 уведомлений
                setDisplayedNotifications(sortedNotifications.slice(0, NOTIFICATIONS_PER_PAGE));
                setHasMore(sortedNotifications.length > NOTIFICATIONS_PER_PAGE);
            } else if (response.data.type !== 'notifications not found') {
                setError('Произошла ошибка загрузки уведомлений');
                console.error('Notification error:', response.data.type);
            }
        } catch (err) {
            setError('Ошибка соединения');
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMoreNotifications = useCallback(() => {
        const nextPage = currentPage + 1;
        const startIndex = 0;
        const endIndex = nextPage * NOTIFICATIONS_PER_PAGE;
        const newNotifications = allNotifications.slice(startIndex, endIndex);

        setDisplayedNotifications(newNotifications);
        setCurrentPage(nextPage);
        setHasMore(endIndex < allNotifications.length);
    }, [currentPage, allNotifications]);

    useEffect(() => {
        const initializeData = async () => {
            await Promise.all([fetchUserData(), fetchNotifications()]);
        };

        initializeData();
    }, [fetchUserData, fetchNotifications]);

    const getNotificationIcon = (type) => {
        const typeLower = type.toLowerCase();

        if (typeLower.includes('отмен')) {
            return '⚠️';
        } else {
            return '🔔';
        }
    };

    const getNotificationColor = (type) => {
        const typeLower = type.toLowerCase();

        if (typeLower.includes('отмен')) {
            return 'var(--notification-warning)';
        }  else {
            return 'var(--notification-default)';
        }
    };

    if (!user || loading) {
        return <Loader />;
    }

    return (
        <div className="notifications-app">
            <main className="notifications-container">
                <header className="notifications-header">
                    <h1 className="notifications-title">Мои уведомления</h1>
                    <p className="notifications-subtitle">
                        {allNotifications.length > 0
                            ? `Всего уведомлений: ${allNotifications.length}`
                            : 'Все уведомления прочитаны'
                        }
                    </p>
                </header>

                {error && (
                    <div className="error-message">
                        <div className="error-icon">⚠️</div>
                        <div className="error-text">{error}</div>
                    </div>
                )}

                <section className="notifications-list">
                    {displayedNotifications.length > 0 ? (
                        <>
                            {displayedNotifications.map((notification, index) => (
                                <NotificationCard
                                    key={index}
                                    notification={notification}
                                    icon={getNotificationIcon(notification.type)}
                                    color={getNotificationColor(notification.type)}
                                />
                            ))}

                            {hasMore && (
                                <div className="load-more-container">
                                    <button
                                        className="load-more-btn"
                                        onClick={loadMoreNotifications}
                                    >
                                        <span>Загрузить еще</span>
                                        <span className="load-more-count">
                      +{Math.min(NOTIFICATIONS_PER_PAGE, allNotifications.length - displayedNotifications.length)}
                    </span>
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <EmptyState />
                    )}
                </section>
            </main>

            <Navbar usertype={user.role} fixedT={user.fixed_trainer} />
        </div>
    );
}

const NotificationCard = ({ notification, icon, color }) => (
    <div className="notification-card" style={{ '--accent-color': color }}>
        <div className="notification-icon" style={{ backgroundColor: color }}>
            {icon}
        </div>

        <div className="notification-content">
            <div className="notification-header">
                <h3 className="notification-type">{notification.type}</h3>
                <span className="notification-date">
          <svg className="date-icon" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
                    {notification.date}
        </span>
            </div>

            <div className="notification-body">
                <p className="notification-description">
                    {notification.description || 'Новое уведомление в системе'}
                </p>
            </div>
        </div>
    </div>
);

const EmptyState = () => (
    <div className="empty-state">
        <div className="empty-state-content">
            <div className="empty-state-icon">🎉</div>
            <h3 className="empty-state-title">Уведомления отсутствуют</h3>
            <p className="empty-state-description">
                Все задачи выполнены! Новые уведомления появятся здесь автоматически
            </p>
        </div>
    </div>
);